import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, AlertCircle, Loader, Star, MessageSquare, Eye } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '../lib/api'
import { formatPrice } from '../utils/formatPrice'
import ImageLightbox from '../components/ImageLightbox'

export default function ProductDetailsPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [product, setProduct] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [lightboxImage, setLightboxImage] = useState<{ src: string; title: string } | null>(null)

  useEffect(() => {
    if (id) {
      fetchProduct()
    }
  }, [id])

  const fetchProduct = async () => {
    try {
      setLoading(true)
      const res = await api.getProduct(id!)
      setProduct(res.data.data)
    } catch (error: any) {
      toast.error('Failed to load product details')
      console.error(error)
      navigate('/products')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-3">
          <Loader className="w-12 h-12 text-[#6D3A0D] animate-spin" />
          <p className="text-gray-600">Loading product details...</p>
        </div>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-3" />
        <p className="text-gray-600">Product not found</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <ImageLightbox
        isOpen={!!lightboxImage}
        src={lightboxImage?.src || null}
        alt={lightboxImage?.title}
        onClose={() => setLightboxImage(null)}
      />

      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/products')}
          className="p-2 border-2 border-[#6D3A0D] text-[#6D3A0D] rounded-lg hover:bg-[#F6EEE7] transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-[#6D3A0D]">{product.name}</h1>
          <p className="text-sm text-gray-500 font-mono">SKU: {product.sku}</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Images Section */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border-2 border-[#F6EEE7] p-6 space-y-4">
            {/* Main Image */}
            {product.mainImage && (
              <div
                className="relative group flex justify-center bg-gray-100 rounded-lg overflow-hidden h-96 cursor-pointer"
                onClick={() => setLightboxImage({ src: product.mainImage, title: product.name })}
              >
                <img
                  src={product.mainImage}
                  alt={product.name}
                  className="object-contain"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-white gap-2 font-semibold">
                  <Eye size={20} /> View Full Image
                </div>
              </div>
            )}

            {/* Gallery Images */}
            {product.images && product.images.length > 0 && (
              <div>
                <h3 className="font-semibold text-[#6D3A0D] mb-3">Product Gallery</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
                  {product.images.map((img: any, idx: number) => (
                    <div
                      key={idx}
                      className="relative group bg-gray-100 rounded-lg overflow-hidden h-16 sm:h-24 cursor-pointer"
                      onClick={() => setLightboxImage({ src: img.imageUrl, title: `${product.name} - Gallery ${idx + 1}` })}
                    >
                      <img
                        src={img.imageUrl}
                        alt={`Product ${idx + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-white">
                        <Eye size={16} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Product Info Section */}
        <div className="space-y-4">
          {/* Pricing */}
          <div className="bg-white rounded-xl border-2 border-[#F6EEE7] p-6">
            <h3 className="font-semibold text-[#6D3A0D] mb-4">Pricing</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Price:</span>
                <span className="font-semibold text-lg text-[#6D3A0D]">
                  {formatPrice(product.price)}
                </span>
              </div>

              {product.cost && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Cost:</span>
                  <span className="font-semibold text-[#6D3A0D]">
                    {formatPrice(product.cost)}
                  </span>
                </div>
              )}

              {product.discountPrice && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Discount Price:</span>
                  <span className="font-semibold text-green-600">
                    {formatPrice(product.discountPrice)}
                  </span>
                </div>
              )}

              {product.discountPercentage && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Discount:</span>
                  <span className="font-semibold text-green-600">
                    {product.discountPercentage}%
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Stock & Status */}
          <div className="bg-white rounded-xl border-2 border-[#F6EEE7] p-6">
            <h3 className="font-semibold text-[#6D3A0D] mb-4">Stock & Status</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Stock:</span>
                <span className={`font-semibold ${product.stock > 5 ? 'text-green-600' : 'text-red-600'}`}>
                  {product.stock} units
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-gray-600">Status:</span>
                <span className="font-semibold text-[#6D3A0D]">
                  {product.isActive ? '✓ Active' : '✗ Inactive'}
                </span>
              </div>

              {product.isFeatured && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Featured:</span>
                  <span className="font-semibold text-[#6D3A0D]">✓ Yes</span>
                </div>
              )}
            </div>
          </div>

          {/* Rating */}
          {product.rating && (
            <div className="bg-white rounded-xl border-2 border-[#F6EEE7] p-6">
              <h3 className="font-semibold text-[#6D3A0D] mb-4">Rating</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-3xl font-bold text-[#6D3A0D]">{product.rating}</span>
                  <span className="text-gray-600">/ 5.0</span>
                </div>
                <p className="text-sm text-gray-600">
                  Based on {product.reviewCount} reviews
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Details Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Product Info */}
        <div className="bg-white rounded-xl border-2 border-[#F6EEE7] p-6">
          <h3 className="font-semibold text-[#6D3A0D] mb-4">Product Information</h3>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-600">SKU</p>
              <p className="font-semibold text-gray-900">{product.sku}</p>
            </div>

            {product.category && (
              <div>
                <p className="text-sm text-gray-600">Category</p>
                <p className="font-semibold text-gray-900">{product.category.name}</p>
              </div>
            )}

            {product.brand && (
              <div>
                <p className="text-sm text-gray-600">Brand</p>
                <p className="font-semibold text-gray-900">{product.brand.name}</p>
              </div>
            )}

            {product.tag && (
              <div>
                <p className="text-sm text-gray-600">Tag</p>
                <p className="font-semibold text-gray-900">{product.tag}</p>
              </div>
            )}

            <div>
              <p className="text-sm text-gray-600">Slug</p>
              <p className="font-semibold text-gray-900 break-all">{product.slug}</p>
            </div>
          </div>
        </div>

        {/* Timestamps */}
        <div className="bg-white rounded-xl border-2 border-[#F6EEE7] p-6">
          <h3 className="font-semibold text-[#6D3A0D] mb-4">Timestamps</h3>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-600">Created</p>
              <p className="font-semibold text-gray-900">
                {new Date(product.createdAt).toLocaleString()}
              </p>
            </div>

            <div>
              <p className="text-sm text-gray-600">Last Updated</p>
              <p className="font-semibold text-gray-900">
                {new Date(product.updatedAt).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="bg-white rounded-xl border-2 border-[#F6EEE7] p-6">
        <h3 className="font-semibold text-[#6D3A0D] mb-4">Description</h3>
        <p className="text-gray-700 whitespace-pre-wrap">{product.description}</p>
      </div>

      {product.shortDescription && (
        <div className="bg-white rounded-xl border-2 border-[#F6EEE7] p-6">
          <h3 className="font-semibold text-[#6D3A0D] mb-4">Short Description</h3>
          <p className="text-gray-700">{product.shortDescription}</p>
        </div>
      )}

      {/* Customer Reviews Section */}
      <div className="bg-white rounded-xl border-2 border-[#F6EEE7] p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare size={20} className="text-[#6D3A0D]" />
            <h3 className="font-semibold text-lg text-[#6D3A0D]">Customer Reviews</h3>
          </div>
          <span className="text-xs font-semibold bg-[#F6EEE7] text-[#6D3A0D] px-3 py-1 rounded-full">
            {product.reviews?.length || 0} Total Reviews
          </span>
        </div>

        {product.reviews && product.reviews.length > 0 ? (
          <div className="space-y-4 pt-2">
            {product.reviews.map((review: any) => (
              <div key={review.id} className="border border-gray-100 bg-[#FAF7F5] rounded-xl p-4 space-y-2">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900 text-sm">
                      {review.user ? `${review.user.firstName || ''} ${review.user.lastName || ''}`.trim() || review.user.email : 'Customer'}
                    </span>
                    {review.user?.email && (
                      <span className="text-xs text-gray-500">({review.user.email})</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex text-amber-400">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={14}
                          className={i < review.rating ? "fill-amber-400 text-amber-400" : "text-gray-300"}
                        />
                      ))}
                    </div>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      review.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                      review.status === 'PENDING' ? 'bg-amber-100 text-amber-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {review.status || 'APPROVED'}
                    </span>
                  </div>
                </div>

                <p className="text-sm text-gray-700 whitespace-pre-line">{review.comment}</p>

                <div className="text-xs text-gray-500 pt-1">
                  Submitted on {new Date(review.createdAt).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-gray-500 text-sm">
            No reviews submitted for this product yet.
          </div>
        )}
      </div>

      {/* SEO Section */}
      <div className="bg-white rounded-xl border-2 border-[#F6EEE7] p-6">
        <h3 className="font-semibold text-[#6D3A0D] mb-4">SEO Information</h3>
        <div className="space-y-4">
          {product.metaTitle && (
            <div>
              <p className="text-sm text-gray-600">Meta Title</p>
              <p className="font-semibold text-gray-900">{product.metaTitle}</p>
            </div>
          )}

          {product.metaDescription && (
            <div>
              <p className="text-sm text-gray-600">Meta Description</p>
              <p className="font-semibold text-gray-900">{product.metaDescription}</p>
            </div>
          )}

          {product.metaKeywords && (
            <div>
              <p className="text-sm text-gray-600">Meta Keywords</p>
              <p className="font-semibold text-gray-900">{product.metaKeywords}</p>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <button
          onClick={() => navigate(`/products`)}
          className="flex-1 px-4 py-2 border-2 border-[#6D3A0D] text-[#6D3A0D] rounded-lg font-semibold hover:bg-[#F6EEE7] transition"
        >
          Back to Products
        </button>
      </div>
    </div>
  )
}
